import dxPopup from 'devextreme/ui/popup';

/**
 * Correção global do truncamento prematuro dos títulos de popups (JOR4-7239).
 *
 * Causa raiz: o dxToolbarBase (usado pela barra de título de todo dxPopup)
 * calcula um `max-width` inline em pixels para o rótulo do título dentro de
 * `_arrangeItems`, a partir da largura da toolbar *no instante do render*.
 * No tema Generic esse cálculo roda uma única vez, de forma síncrona, ANTES
 * do popup atingir sua largura final (larguras percentuais / fullScreen) e
 * antes da fonte web carregar — o re-arranjo pós-animação existe apenas para o
 * tema Material (`if (isMaterial())`). Assim o `max-width` sai menor que o
 * espaço real, o `text-overflow: ellipsis` do `.dx-toolbar-label > div` entra
 * em ação e nunca mais é recalculado: o título trunca mesmo sobrando espaço.
 * (Abrir/fechar o F12 disparava um resize que forçava o recálculo — daí o
 * título aparecer inteiro só nesse momento.)
 *
 * Correção: quando o título é renderizado, recalculamos o `max-width` do rótulo
 * com base no espaço realmente disponível (largura da barra menos o botão
 * fechar e demais itens) e o mantemos sincronizado via ResizeObserver
 * (redimensionamento de janela / fullScreen) e em `document.fonts.ready`
 * (carregamento da fonte). O rótulo passa a truncar somente quando o texto de
 * fato excede o espaço; nesse caso exibimos o texto completo via atributo
 * `title` (tooltip nativo do navegador), removendo-o quando não há truncamento.
 *
 * Ponto de acoplamento: usamos a API pública `onTitleRendered` como *default*
 * global do dxPopup (via `defaultOptions`), em vez de sobrescrever métodos
 * internos. Vale para TODOS os popups do sistema — legados
 * (`devextreme/ui/popup` via PopUpPadrao) e React (`devextreme-react/popup`),
 * além dos popups internos de outros widgets que usam dxPopup — sem alterar
 * cada tela. Telas que definirem seu próprio `onTitleRendered` sobrescrevem
 * este default (hoje nenhuma define).
 *
 * Escopo defensivo: só atuamos sobre o layout LTR padrão (título na seção
 * `.dx-toolbar-before`). Títulos centralizados (iOS) e RTL são deixados
 * intocados, preservando o comportamento original do DevExtreme nesses casos.
 */

const OBSERVER_KEY = '__jor4TituloObserver';
const FONTS_HOOK_KEY = '__jor4TituloFontsHook';
const DISPOSE_HOOK_KEY = '__jor4TituloDisposeHook';

/** Normaliza para um elemento DOM (o titleElement pode vir como jQuery). */
function paraElementoDom(el) {
    if (!el) return null;
    return el.get ? el.get(0) : el;
}

/** Localiza o elemento interno que recebe o ellipsis dentro do rótulo. */
function getElementoEllipsis(label) {
    return (
        label.querySelector('.dx-toolbar-item-content > div') ||
        label.querySelector(':scope > div') ||
        label.querySelector('.dx-toolbar-item-content') ||
        label
    );
}

/**
 * Recalcula o max-width do título e mantém o tooltip apenas quando truncado.
 * @param {HTMLElement} titleEl elemento `.dx-popup-title` (raiz da toolbar)
 * @param {boolean} rtlEnabled quando true, não fazemos nada (layout espelhado)
 */
function ajustarTitulo(titleEl, rtlEnabled) {
    if (rtlEnabled) return;

    const container = titleEl.querySelector('.dx-toolbar-items-container');
    // Só tratamos o título posicionado à esquerda (comportamento generic/desktop).
    const label = titleEl.querySelector('.dx-toolbar-before .dx-toolbar-label');
    if (!container || !label) return;

    const inner = getElementoEllipsis(label);
    const after = titleEl.querySelector('.dx-toolbar-after');

    const containerWidth = container.clientWidth;
    if (containerWidth === 0) return; // ainda oculto/sem layout — o observer refará

    const containerRect = container.getBoundingClientRect();
    const afterWidth = after ? after.getBoundingClientRect().width : 0;
    // Deslocamento à esquerda do rótulo (cobre eventuais itens antes dele).
    const labelLeft = label.getBoundingClientRect().left - containerRect.left;

    const estilo = getComputedStyle(label);
    const padding =
        (parseFloat(estilo.paddingLeft) || 0) + (parseFloat(estilo.paddingRight) || 0);

    const disponivel = Math.max(containerWidth - afterWidth - labelLeft - padding, 0);
    if (disponivel === 0) return;

    // `!important` para prevalecer sobre o max-width inline que o dxToolbar
    // recalcula (com valor defasado) nos seus próprios eventos de resize.
    label.style.setProperty('max-width', `${disponivel}px`, 'important');

    // Tooltip somente quando o texto realmente não coube.
    const truncado = inner.scrollWidth - inner.clientWidth > 1;
    const texto = (inner.textContent || '').trim();
    if (truncado && texto) {
        inner.setAttribute('title', texto);
    } else {
        inner.removeAttribute('title');
    }
}

/**
 * Handler de `onTitleRendered`: dispara o ajuste no momento certo e o mantém
 * sincronizado com resize, fullScreen e carregamento de fonte.
 */
function aoRenderizarTitulo(e) {
    try {
        const titleEl = paraElementoDom(e.titleElement);
        const popup = e.component;
        if (!titleEl || !popup) return;

        const rtlEnabled = !!(popup.option && popup.option('rtlEnabled'));
        const aplicar = () => ajustarTitulo(titleEl, rtlEnabled);
        const agendar = () =>
            typeof requestAnimationFrame === 'function'
                ? requestAnimationFrame(aplicar)
                : aplicar();

        // 1) Primeiro cálculo após o layout inicial.
        agendar();

        // 2) Recalcula quando a fonte web terminar de carregar — uma vez por popup.
        if (!popup[FONTS_HOOK_KEY] && document.fonts && document.fonts.ready) {
            popup[FONTS_HOOK_KEY] = true;
            document.fonts.ready.then(agendar).catch(() => {});
        }

        // 3) Mantém sincronizado com resize / fullScreen / troca de itens.
        //    Desconecta observer anterior (o título é recriado a cada render).
        if (popup[OBSERVER_KEY]) {
            popup[OBSERVER_KEY].disconnect();
            popup[OBSERVER_KEY] = null;
        }

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(agendar);
            observer.observe(titleEl);
            const after = titleEl.querySelector('.dx-toolbar-after');
            if (after) observer.observe(after);
            popup[OBSERVER_KEY] = observer;

            // Registra a limpeza uma única vez por instância de popup.
            if (!popup[DISPOSE_HOOK_KEY] && typeof popup.on === 'function') {
                popup[DISPOSE_HOOK_KEY] = true;
                popup.on('disposing', () => {
                    if (popup[OBSERVER_KEY]) {
                        popup[OBSERVER_KEY].disconnect();
                        popup[OBSERVER_KEY] = null;
                    }
                });
            }
        }
    } catch (err) {
        // Uma falha aqui jamais pode impedir a renderização do popup.
        console.error('Falha ao ajustar título do popup:', err);
    }
}

/**
 * Instala a correção como default global do dxPopup. Deve ser chamado uma
 * única vez no bootstrap, antes de qualquer popup ser criado.
 */
export function instalarCorrecaoTituloPopup() {
    dxPopup.defaultOptions({
        options: {
            onTitleRendered: aoRenderizarTitulo,
        },
    });
}
